import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useField } from 'formik';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import styles from './PasswordHelpers.module.scss';

type Props = {
  name: string;
  classes?: Partial<{
    container: string;
  }>;
};

export function PasswordHelpers({ name, classes }: Props): JSX.Element {
  const { t } = useTranslation();
  const [field, meta] = useField({ name });
  const { value } = field;
  const { touched } = meta;

  const validationRules = {
    minChar: value.length >= 8,
    minNumber: /[0-9]/.test(value),
    minUppercase: /[A-Z]/.test(value),
    minLowercase: /[a-z]/.test(value),
    minSpecialChar: /[à-öø-þÀ-ÖØ-Þ²~\-_^@"&°+=€¨^£$%µ*<>,?.;:§!#{(|\\[\])}]/.test(
      value
    ),
  };

  const validationMessages = [
    { rule: 'minChar', message: t('LABEL_PASSWORD_SPECIFICITY_CHARACTERS') },
    { rule: 'minNumber', message: t('LABEL_PASSWORD_SPECIFICITY_NUMBER') },
    {
      rule: 'minUppercase',
      message: t('LABEL_PASSWORD_SPECIFICITY_UPPERCASE'),
    },
    {
      rule: 'minLowercase',
      message: t('LABEL_PASSWORD_SPECIFICITY_LOWERCASE'),
    },
    {
      rule: 'minSpecialChar',
      message: t('LABEL_PASSWORD_SPECIFICITY_SPECIALCHARACTERS'),
    },
  ];

  const allValid = Object.values(validationRules).every(Boolean);

  if (allValid) {
    return <></>;
  }

  return (
    <div className={classNames(styles.indicatorsContainer, classes?.container)}>
      <div className={styles.safetyContent}>{t('LABEL_PASSWORD_SAFETY')}</div>
      <ul className={styles.content}>
        {validationMessages.map(({ rule, message }) => {
          const isValid = validationRules[rule];
          const showIcon = (isValid && (value || touched)) || touched;
          const style =
            isValid && showIcon
              ? styles.validate
              : touched && styles.notValidate;

          return (
            <li className={style || ''} key={rule}>
              <span
                className={classNames(styles.iconWrapper, {
                  [styles.visible]: showIcon,
                })}
              >
                <FontAwesomeIcon
                  icon={isValid ? faCheck : faTimes}
                  className={
                    isValid
                      ? styles.iconCheckedValidator
                      : styles.iconCloseValidator
                  }
                />
              </span>
              <span>{` ${message}`}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
